import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Mail, MapPin, Clock, CheckCircle2, ArrowRight, AlertCircle } from 'lucide-react';
import { PageHero, FadeIn, SectionLabel } from '../components/Shared';
import AIPlanner from '../components/AIPlanner';
import VortexBg from '../components/bg/VortexBg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'security-plan', label: 'Security Plan' },
    { value: 'responsive-design', label: 'Responsive Design' },
    { value: 'seo-optimisation', label: 'SEO Optimisation' },
    { value: 'performance', label: 'Performance' },
    { value: 'content-management', label: 'Content Management' },
    { value: 'analytics-tracking', label: 'Analytics & Tracking' },
    { value: 'gdpr-compliance', label: 'GDPR Compliance' },
    { value: 'both', label: 'Both' },
    { value: 'other', label: 'Other' },
];

const BUDGET_OPTIONS = [
    { value: 'under-500', label: 'Under £500' },
    { value: '500-900', label: '£500 - £900' },
    { value: '900-1500', label: '£900 - £1,500' },
    { value: '1500-plus', label: '£1,500+' },
    { value: 'not-sure', label: 'Not Sure' },
];

const initial = {
    full_name: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    project_details: '',
    gdpr_consent: false,
};

const fieldStyles =
    'contact-input w-full rounded-xl px-4 py-3 text-[#1A1A2E] focus:outline-none transition-all duration-200';

const Contact = () => {
    const location = useLocation();
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const plan = params.get('plan');
        const serviceParam = params.get('service');
        if (plan) {
            const detail =
                plan === 'security'
                    ? "I'm interested in the £49/month AI Security Plan."
                    : `I'm interested in the ${plan.charAt(0).toUpperCase() + plan.slice(1)} package.`;
            setForm((f) => ({
                ...f,
                service: plan === 'security' ? 'security-plan' : 'web-development',
                project_details: detail,
            }));
        } else if (serviceParam) {
            // Deep-link from the hero orbital service detail panel: ?service=<slug>.
            const match = SERVICE_OPTIONS.find((o) => o.value === serviceParam);
            if (match) {
                setForm((f) => ({
                    ...f,
                    service: match.value,
                    project_details: f.project_details ||
                        `I'm interested in your ${match.label} service.`,
                }));
            }
        }
    }, [location.search]);

    const update = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.gdpr_consent) {
            setError('Please provide GDPR consent to continue.');
            return;
        }
        if (!form.service || !form.budget) {
            setError('Please select both a service and a budget range.');
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/contact`, form);
            setSuccess(true);
            setForm(initial);
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.detail;
            let msg = 'Something went wrong sending your message. Please try again.';
            if (typeof detail === 'string') msg = detail;
            else if (Array.isArray(detail) && detail.length > 0) msg = 'Please double-check your details and try again.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="bg-white text-[#1A1A2E] pb-24" data-testid="contact-page">
            <PageHero
                label="Contact"
                title="Let's talk."
                subtitle="Ready to start your project? Get in touch and we'll respond within 24 hours."
                testId="contact-hero"
                bg={<VortexBg />}
            />

            <section className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Form */}
                    <FadeIn>
                        <div
                            className="contact-form-card rounded-[24px] p-6 md:p-10 mx-auto w-full"
                            style={{
                                maxWidth: '720px',
                                margin: '24px auto',
                                background: 'rgba(255, 255, 255, 0.55)',
                                backdropFilter: 'blur(20px) saturate(140%)',
                                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                                border: '1.5px solid rgba(255, 255, 255, 0.4)',
                                boxShadow:
                                    '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                                overflow: 'visible',
                            }}
                            data-testid="contact-form-container"
                        >
                            <SectionLabel>Send a message</SectionLabel>
                            <h2 className="mt-4 font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#1A1A2E]">
                                Tell us about your project.
                            </h2>

                            {success ? (
                                <div
                                    className="mt-10 p-6 border border-[#00B89E]/40 bg-[#E6FBF6] rounded-2xl"
                                    data-testid="contact-success"
                                >
                                    <div className="flex items-start gap-4">
                                        <CheckCircle2 className="w-6 h-6 text-[#00B89E] shrink-0 mt-0.5" />
                                        <div>
                                            <h3 className="font-display text-lg font-semibold tracking-tight text-[#1A1A2E]">
                                                Message sent
                                            </h3>
                                            <p className="mt-2 text-[14px] text-[#4A4A5A] leading-relaxed">
                                                Thanks for reaching out. We'll review your enquiry and
                                                respond within 24 hours.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setSuccess(false)}
                                                className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#00B89E]"
                                                data-testid="contact-send-another"
                                            >
                                                Send another message
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="contact-form">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-[13px] font-semibold text-[#1A1A2E] mb-2">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                className={fieldStyles}
                                                value={form.full_name}
                                                onChange={(e) => update('full_name', e.target.value)}
                                                placeholder="Jane Smith"
                                                data-testid="contact-input-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[13px] font-semibold text-[#1A1A2E] mb-2">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                required
                                                className={fieldStyles}
                                                value={form.email}
                                                onChange={(e) => update('email', e.target.value)}
                                                placeholder="you@business.co.uk"
                                                data-testid="contact-input-email"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[13px] font-semibold text-[#1A1A2E] mb-2">
                                            Phone Number (optional)
                                        </label>
                                        <input
                                            type="tel"
                                            className={fieldStyles}
                                            value={form.phone}
                                            onChange={(e) => update('phone', e.target.value)}
                                            placeholder="+44 7700 900000"
                                            data-testid="contact-input-phone"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="block text-[13px] font-semibold text-[#1A1A2E] mb-2">
                                                Service Interested In *
                                            </label>
                                            <select
                                                required
                                                value={form.service}
                                                onChange={(e) => update('service', e.target.value)}
                                                className={`${fieldStyles} appearance-none cursor-pointer pr-10`}
                                                style={{
                                                    backgroundImage:
                                                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%231A1A2E' stroke-width='2.5'><polyline points='6 9 12 15 18 9'/></svg>\")",
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 14px center',
                                                }}
                                                data-testid="contact-select-service"
                                            >
                                                <option value="" disabled>Select a service</option>
                                                {SERVICE_OPTIONS.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[13px] font-semibold text-[#1A1A2E] mb-2">
                                                Budget Range *
                                            </label>
                                            <select
                                                required
                                                value={form.budget}
                                                onChange={(e) => update('budget', e.target.value)}
                                                className={`${fieldStyles} appearance-none cursor-pointer pr-10`}
                                                style={{
                                                    backgroundImage:
                                                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%231A1A2E' stroke-width='2.5'><polyline points='6 9 12 15 18 9'/></svg>\")",
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 14px center',
                                                }}
                                                data-testid="contact-select-budget"
                                            >
                                                <option value="" disabled>Select a range</option>
                                                {BUDGET_OPTIONS.map((b) => (
                                                    <option key={b.value} value={b.value}>{b.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[13px] font-semibold text-[#1A1A2E] mb-2">
                                            Project Details *
                                        </label>
                                        <textarea
                                            required
                                            rows={5}
                                            className={`${fieldStyles} resize-none`}
                                            value={form.project_details}
                                            onChange={(e) => update('project_details', e.target.value)}
                                            placeholder="Tell us a bit about your business, goals, and timeline…"
                                            data-testid="contact-input-details"
                                        />
                                    </div>

                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <span className="relative shrink-0 mt-0.5">
                                            <input
                                                type="checkbox"
                                                checked={form.gdpr_consent}
                                                onChange={(e) => update('gdpr_consent', e.target.checked)}
                                                className="peer appearance-none w-5 h-5 rounded checked:bg-[#3B82F6] checked:border-[#3B82F6] cursor-pointer transition-colors"
                                                style={{
                                                    border: '1.5px solid rgba(0, 30, 60, 0.2)',
                                                    background: form.gdpr_consent
                                                        ? '#3B82F6'
                                                        : 'rgba(255, 255, 255, 0.9)',
                                                }}
                                                data-testid="contact-checkbox-gdpr"
                                            />
                                            <CheckCircle2 className="w-3.5 h-3.5 text-white absolute top-[3px] left-[3px] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                                        </span>
                                        <span className="text-[13px] text-[#4A4A5A] leading-relaxed">
                                            I agree to REVOLO AI WEB processing my data to respond to
                                            this enquiry.
                                        </span>
                                    </label>

                                    {error && (
                                        <div
                                            className="flex items-start gap-3 p-4 border border-red-200 bg-red-50 rounded-xl"
                                            data-testid="contact-error"
                                        >
                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                            <p className="text-[14px] text-red-700">{error}</p>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#1D4ED8] text-white font-semibold text-[15px] px-7 py-3.5 rounded-full transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                        style={{
                                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.30)',
                                        }}
                                        data-testid="contact-submit"
                                    >
                                        {submitting ? 'Sending…' : 'Send Message'}
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </form>
                            )}
                        </div>
                    </FadeIn>

                    {/* AI Planner — replaces the old info column */}
                    <FadeIn delay={0.1}>
                        <div data-testid="contact-planner-wrap">
                            <SectionLabel>Or use AI</SectionLabel>
                            <h2 className="mt-4 mb-5 font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#1A1A2E]">
                                Or plan your website instantly with AI.
                            </h2>
                            <AIPlanner testId="planner-contact" height="h-[640px]" />
                        </div>
                    </FadeIn>
                </div>

                {/* Direct contact info — row beneath the two-column area */}
                <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-10">
                    <FadeIn delay={0.2}>
                        <div
                            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
                            data-testid="contact-info-row"
                        >
                            <div className="p-6 bg-[#F7F7F8] border border-[#E8E8EC] rounded-2xl flex items-center gap-4">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-[#E8E8EC] shrink-0">
                                    <Mail className="w-4 h-4 text-[#7B61FF]" />
                                </span>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B6B7A]">
                                        Email
                                    </div>
                                    <a
                                        href="mailto:hello@revoloaiweb.com"
                                        className="block mt-0.5 text-[15px] text-[#1A1A2E] hover:text-[#7B61FF] transition-colors"
                                        data-testid="contact-info-email"
                                    >
                                        hello@revoloaiweb.com
                                    </a>
                                </div>
                            </div>
                            <div className="p-6 bg-[#F7F7F8] border border-[#E8E8EC] rounded-2xl flex items-center gap-4">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-[#E8E8EC] shrink-0">
                                    <MapPin className="w-4 h-4 text-[#7B61FF]" />
                                </span>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B6B7A]">
                                        Location
                                    </div>
                                    <span className="block mt-0.5 text-[15px] text-[#1A1A2E]">
                                        United Kingdom
                                    </span>
                                </div>
                            </div>
                            <div className="p-6 bg-[#F7F7F8] border border-[#E8E8EC] rounded-2xl flex items-center gap-4">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-[#E8E8EC] shrink-0">
                                    <Clock className="w-4 h-4 text-[#7B61FF]" />
                                </span>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B6B7A]">
                                        Response Time
                                    </div>
                                    <span className="block mt-0.5 text-[15px] text-[#1A1A2E]">
                                        Within 24 hours
                                    </span>
                                </div>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.3}>
                        <div
                            className="mt-6 p-6 bg-[#F2F0FE] rounded-3xl text-center sm:text-left sm:flex sm:items-center sm:justify-between gap-6"
                            data-testid="contact-call-card"
                        >
                            <p className="text-[15px] text-[#1A1A2E]">
                                Prefer a quick chat? Book a free 15-minute discovery call.
                            </p>
                            <a
                                href="https://calendly.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 sm:mt-0 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#7B61FF]"
                                data-testid="contact-book-call"
                            >
                                Book a Call <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Contact;
